import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Building2, Home, KeyRound, Loader2, Lock, LogOut, Mail, Plus, Smartphone, Ticket, Trash2, UploadCloud } from "lucide-react";
import { useAuth } from "@/App";
import Logo from "@/components/Logo";
import { APK_DOWNLOAD_URL, formatApiErrorDetail } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm() {
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-[#12263a] px-4" data-testid="panel-login">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-md">
        <div className="flex justify-center">
          <Logo variant="light" />
        </div>
        <h1 className="mt-6 text-center font-serif text-2xl font-semibold text-white">Acceso equipo</h1>
        <p className="mt-1.5 text-center text-sm text-white/55">Panel privado de Nidoria</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-white/60">
              <Mail className="h-3.5 w-3.5" /> Email
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="panel-login-email"
              className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-[#14b8a6]"
              placeholder="equipo@nidoria.es"
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-white/60">
              <Lock className="h-3.5 w-3.5" /> Contraseña
            </span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="panel-login-password"
              className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus:border-[#14b8a6]"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </label>

          {error && (
            <p className="rounded-2xl bg-red-500/10 px-4 py-3 text-xs font-bold text-red-300" data-testid="panel-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="panel-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#14b8a6] py-3.5 text-sm font-extrabold text-[#07332f] transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Entrar al panel
          </button>
        </form>

        <a href="/" className="mt-6 block text-center text-xs font-bold text-white/45 transition-colors duration-200 hover:text-[#14b8a6]" data-testid="panel-back-home">
          Volver a nidoria.es
        </a>
      </div>
    </div>
  );
}

function ApkCard() {
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState("");
  const [linkEditing, setLinkEditing] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [message, setMessage] = useState(null);
  const inputRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [infoRes, settingsRes] = await Promise.all([
        axios.get(`${API}/app/apk/info`),
        axios.get(`${API}/app/settings`),
      ]);
      setInfo(infoRes.data);
      setSettings(settingsRes.data);
      setUrl(settingsRes.data.url || "");
    } catch (err) {
      setInfo({ available: false });
      setSettings({ mode: "apk", url: "" });
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveSettings = async (mode, linkUrl) => {
    setSavingMode(true);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/app/settings`, { mode, url: linkUrl }, { withCredentials: true });
      setSettings(data);
      setMessage({
        ok: true,
        text:
          mode === "link"
            ? "El botón de la web ahora abre tu enlace."
            : "El botón de la web ahora descarga la APK directa.",
      });
    } catch (err) {
      setMessage({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSavingMode(false);
    }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/app/apk`, formData, { withCredentials: true });
      setMessage({ ok: true, text: "APK publicada. El botón de la web ya la descarga directa." });
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await loadAll();
    } catch (err) {
      setMessage({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setUploading(false);
    }
  };

  const mode = settings?.mode || "apk";
  const showLink = linkEditing || mode === "link";
  const sizeMb = info?.available ? `${(info.size / 1048576).toFixed(1).replace(".", ",")} MB` : null;
  const statusText =
    mode === "link"
      ? settings?.url
        ? "Enlace activo"
        : "Falta enlace"
      : info?.available
        ? `Publicada · ${sizeMb}`
        : "Sin publicar";
  const statusActive = mode === "link" ? Boolean(settings?.url) : Boolean(info?.available);

  const modeBtnCls = (active) =>
    `rounded-2xl border p-4 text-left transition-colors duration-200 ${
      active
        ? "border-[#0f766e] bg-[#0f766e]/5 dark:border-[#14b8a6] dark:bg-[#14b8a6]/10"
        : "border-[#12263a]/15 hover:border-[#0f766e]/50 dark:border-white/15 dark:hover:border-[#14b8a6]/50"
    }`;

  return (
    <div className="mt-10 rounded-3xl border border-[#12263a]/10 bg-white p-6 dark:border-white/10 dark:bg-[#102336]" data-testid="apk-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2.5 font-serif text-xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">
          <span className="rounded-2xl bg-[#0f766e]/10 p-2.5 text-[#0f766e] dark:text-[#14b8a6]">
            <Smartphone className="h-4 w-4" />
          </span>
          App Android (botón de descarga)
        </h2>
        <span
          className={`rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide ${
            statusActive ? "bg-[#0f766e]/10 text-[#0f766e] dark:text-[#14b8a6]" : "bg-[#f59e0b]/10 text-[#b45309]"
          }`}
          data-testid="apk-status"
        >
          {statusText}
        </span>
      </div>

      <p className="mt-3 text-sm text-[#12263a]/55 dark:text-[#f7f4ee]/50">
        Elige qué hace el botón de Android de la portada: descargar la APK directamente o abrir un enlace (p. ej. Google Play).
      </p>

      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => {
            setLinkEditing(false);
            saveSettings("apk", settings?.url || "");
          }}
          disabled={savingMode}
          className={modeBtnCls(!showLink)}
          data-testid="apk-mode-direct"
        >
          <p className="text-sm font-extrabold text-[#12263a] dark:text-[#f7f4ee]">Descarga directa (APK)</p>
          <p className="mt-1 text-xs leading-relaxed text-[#12263a]/50 dark:text-[#f7f4ee]/45">
            El botón descarga al momento la APK que subas aquí.
          </p>
        </button>
        <button
          type="button"
          onClick={() => setLinkEditing(true)}
          disabled={savingMode}
          className={modeBtnCls(showLink)}
          data-testid="apk-mode-link"
        >
          <p className="text-sm font-extrabold text-[#12263a] dark:text-[#f7f4ee]">Enlace externo</p>
          <p className="mt-1 text-xs leading-relaxed text-[#12263a]/50 dark:text-[#f7f4ee]/45">
            El botón abre el enlace que pongas, por ejemplo Google Play.
          </p>
        </button>
      </div>

      {showLink && (
        <div className="mt-4 flex flex-wrap gap-2.5" data-testid="apk-link-form">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://play.google.com/store/apps/details?id=…"
            data-testid="apk-link-input"
            className="min-w-0 flex-1 rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
          />
          <button
            type="button"
            onClick={() => saveSettings("link", url)}
            disabled={savingMode || !url.trim()}
            data-testid="apk-link-save"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#0f766e] px-5 py-3 text-sm font-extrabold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingMode && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar enlace
          </button>
        </div>
      )}

      {!showLink && (
        <>
          <p className="mt-5 text-sm text-[#12263a]/55 dark:text-[#f7f4ee]/50" data-testid="apk-info-text">
            {info?.available
              ? `Archivo actual: ${info.filename} (${sizeMb}), subido el ${new Date(info.uploaded_at).toLocaleDateString("es-ES")}.`
              : "Sube la APK de Nidoria y el botón de la web la descargará directamente."}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              className="hidden"
              data-testid="apk-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="apk-choose-button"
              className="inline-flex items-center gap-2 rounded-2xl border border-[#12263a]/15 px-5 py-3 text-sm font-extrabold text-[#12263a] transition-colors duration-200 hover:border-[#0f766e] hover:text-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:hover:border-[#14b8a6] dark:hover:text-[#14b8a6]"
            >
              <UploadCloud className="h-4 w-4" />
              {file ? file.name : "Elegir archivo .apk"}
            </button>
            <button
              type="button"
              onClick={upload}
              disabled={!file || uploading}
              data-testid="apk-upload-button"
              className="inline-flex items-center gap-2 rounded-2xl bg-[#0f766e] px-5 py-3 text-sm font-extrabold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
              {uploading ? "Subiendo…" : "Subir APK"}
            </button>
            {info?.available && (
              <a
                href={APK_DOWNLOAD_URL}
                data-testid="apk-test-download"
                className="text-sm font-bold text-[#0f766e] underline decoration-[#14b8a6] underline-offset-4 dark:text-[#14b8a6]"
              >
                Probar descarga
              </a>
            )}
          </div>
        </>
      )}

      {message && (
        <p
          className={`mt-4 rounded-2xl px-4 py-3 text-xs font-bold ${
            message.ok ? "bg-[#0f766e]/10 text-[#0f766e] dark:text-[#14b8a6]" : "bg-red-500/10 text-red-500"
          }`}
          data-testid="apk-message"
        >
          {message.text}
        </p>
      )}
    </div>
  );
}

function ReservationsCard() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ property_title: "", location: "", image_url: "" });
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/visits/reservations`, { withCredentials: true });
      setItems(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    setNewCode("");
    try {
      const { data } = await axios.post(`${API}/visits/reservations`, form, { withCredentials: true });
      setNewCode(data.code);
      setForm({ property_title: "", location: "", image_url: "" });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (code) => {
    try {
      await axios.delete(`${API}/visits/reservations/${code}`, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-10 rounded-3xl border border-[#12263a]/10 bg-white p-6 dark:border-white/10 dark:bg-[#102336]" data-testid="reservations-card">
      <h2 className="flex items-center gap-2.5 font-serif text-xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">
        <span className="rounded-2xl bg-[#0f766e]/10 p-2.5 text-[#0f766e] dark:text-[#14b8a6]">
          <Ticket className="h-4 w-4" />
        </span>
        Números de reserva
      </h2>
      <p className="mt-3 text-sm text-[#12263a]/55 dark:text-[#f7f4ee]/50">
        Genera un código, pásalo por WhatsApp junto a la página <span className="font-bold">/visita</span> y la persona verá su reserva con la ubicación y la foto del piso.
      </p>

      <form onSubmit={create} className="mt-5 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <input
          required
          value={form.property_title}
          onChange={(e) => setForm({ ...form, property_title: e.target.value })}
          placeholder="Inmueble (p. ej. Piso luminoso)"
          data-testid="reservation-form-title"
          className="rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
        />
        <input
          required
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          placeholder="Ubicación (p. ej. Ruzafa, Valencia)"
          data-testid="reservation-form-location"
          className="rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
        />
        <button
          type="submit"
          disabled={creating}
          data-testid="reservation-form-submit"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0f766e] px-5 py-3 text-sm font-extrabold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Generar código
        </button>
        <input
          value={form.image_url}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
          placeholder="URL de la foto del piso (opcional)"
          data-testid="reservation-form-image"
          className="rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6] sm:col-span-3"
        />
      </form>

      {newCode && (
        <p className="mt-4 rounded-2xl bg-[#0f766e]/10 px-4 py-3 text-sm font-bold text-[#0f766e] dark:text-[#14b8a6]" data-testid="reservation-new-code">
          Código generado: <span className="font-mono text-base">{newCode}</span>
        </p>
      )}
      {error && (
        <p className="mt-4 rounded-2xl bg-red-500/10 px-4 py-3 text-xs font-bold text-red-500" data-testid="reservation-error">{error}</p>
      )}

      <div className="mt-5 space-y-3" data-testid="reservation-list">
        {items.length === 0 && (
          <p className="text-sm text-[#12263a]/50 dark:text-[#f7f4ee]/45" data-testid="reservation-empty">
            Todavía no hay códigos generados.
          </p>
        )}
        {items.map((item) => (
          <div
            key={item.code}
            className="flex items-center gap-4 rounded-2xl border border-[#12263a]/8 bg-[#f7f4ee]/60 p-3.5 dark:border-white/8 dark:bg-white/5"
            data-testid={`reservation-row-${item.code}`}
          >
            <span className="rounded-xl bg-[#12263a] px-3 py-1.5 font-mono text-sm font-bold text-[#14b8a6]">{item.code}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-[#12263a] dark:text-[#f7f4ee]">{item.property_title}</p>
              <p className="truncate text-xs font-semibold text-[#12263a]/50 dark:text-[#f7f4ee]/45">{item.location}</p>
            </div>
            <button
              onClick={() => remove(item.code)}
              aria-label={`Eliminar ${item.code}`}
              data-testid={`reservation-delete-${item.code}`}
              className="rounded-full p-2 text-[#12263a]/35 transition-colors duration-200 hover:bg-red-500/10 hover:text-red-500 dark:text-[#f7f4ee]/35"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [form, setForm] = useState({ title: "", kind: "alquiler", price: "", city: "", image_url: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [statsRes, listingsRes] = await Promise.all([
        axios.get(`${API}/panel/stats`, { withCredentials: true }),
        axios.get(`${API}/listings`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setListings(listingsRes.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addListing = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/listings`, form, { withCredentials: true });
      setForm({ title: "", kind: "alquiler", price: "", city: "", image_url: "" });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeListing = async (id) => {
    try {
      await axios.delete(`${API}/listings/${id}`, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const statCards = [
    { label: "Anuncios activos", value: stats?.total, icon: Building2, testid: "stat-total" },
    { label: "En alquiler", value: stats?.alquiler, icon: KeyRound, testid: "stat-alquiler" },
    { label: "En venta", value: stats?.venta, icon: Home, testid: "stat-venta" },
  ];

  return (
    <div className="min-h-screen bg-[#f7f4ee] dark:bg-[#0c1a29]" data-testid="panel-dashboard">
      <header className="border-b border-[#12263a]/10 bg-white dark:border-white/10 dark:bg-[#102336]">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Logo variant="dark" />
          <button
            onClick={logout}
            data-testid="panel-logout-button"
            className="inline-flex items-center gap-2 rounded-xl border border-[#12263a]/15 px-4 py-2 text-xs font-extrabold text-[#12263a] transition-colors duration-200 hover:border-[#0f766e] hover:text-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:hover:border-[#14b8a6] dark:hover:text-[#14b8a6]"
          >
            <LogOut className="h-3.5 w-3.5" />
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h1 className="font-serif text-3xl font-semibold text-[#12263a] dark:text-[#f7f4ee]" data-testid="panel-greeting">
          Hola, {user?.name || "equipo"}
        </h1>
        <p className="mt-1.5 text-sm text-[#12263a]/55 dark:text-[#f7f4ee]/50">
          Gestiona los anuncios, la app y las reservas de Nidoria.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {statCards.map((card) => (
            <div key={card.testid} className="rounded-3xl border border-[#12263a]/10 bg-white p-6 dark:border-white/10 dark:bg-[#102336]" data-testid={card.testid}>
              <span className="inline-flex rounded-2xl bg-[#0f766e]/10 p-2.5 text-[#0f766e] dark:text-[#14b8a6]">
                <card.icon className="h-4 w-4" />
              </span>
              <p className="mt-3 font-serif text-3xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">
                {card.value ?? "—"}
              </p>
              <p className="text-xs font-bold text-[#12263a]/50 dark:text-[#f7f4ee]/45">{card.label}</p>
            </div>
          ))}
        </div>

        <ApkCard />

        <ReservationsCard />

        <div className="mt-10 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <form onSubmit={addListing} className="h-fit rounded-3xl border border-[#12263a]/10 bg-white p-6 dark:border-white/10 dark:bg-[#102336]" data-testid="listing-form">
            <h2 className="font-serif text-xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">Nuevo anuncio</h2>
            <div className="mt-5 space-y-3.5">
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Título (p. ej. Piso luminoso)"
                data-testid="listing-form-title"
                className="w-full rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  data-testid="listing-form-kind"
                  className="rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-[#0f766e] dark:border-white/15 dark:bg-[#102336] dark:text-[#f7f4ee]"
                >
                  <option value="alquiler">Alquiler</option>
                  <option value="venta">Venta</option>
                </select>
                <input
                  required
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="Precio (750 €/mes)"
                  data-testid="listing-form-price"
                  className="rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
                />
              </div>
              <input
                required
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Ciudad"
                data-testid="listing-form-city"
                className="w-full rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
              />
              <input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="URL de la foto (opcional)"
                data-testid="listing-form-image"
                className="w-full rounded-2xl border border-[#12263a]/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#0f766e] dark:border-white/15 dark:text-[#f7f4ee] dark:focus:border-[#14b8a6]"
              />
              <button
                type="submit"
                disabled={saving}
                data-testid="listing-form-submit"
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0f766e] py-3 text-sm font-extrabold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Publicar anuncio
              </button>
            </div>
          </form>

          <div className="rounded-3xl border border-[#12263a]/10 bg-white p-6 dark:border-white/10 dark:bg-[#102336]" data-testid="listing-list">
            <h2 className="font-serif text-xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">Anuncios publicados</h2>
            {error && (
              <p className="mt-4 rounded-2xl bg-red-500/10 px-4 py-3 text-xs font-bold text-red-500" data-testid="panel-error">{error}</p>
            )}
            <div className="mt-5 space-y-3">
              {listings.length === 0 && (
                <p className="text-sm text-[#12263a]/50 dark:text-[#f7f4ee]/45" data-testid="listing-empty">
                  Todavía no hay anuncios. Publica el primero desde el formulario.
                </p>
              )}
              {listings.map((listing) => (
                <div
                  key={listing.id}
                  className="flex items-center gap-4 rounded-2xl border border-[#12263a]/8 bg-[#f7f4ee]/60 p-3.5 dark:border-white/8 dark:bg-white/5"
                  data-testid={`listing-row-${listing.id}`}
                >
                  {listing.image_url ? (
                    <img src={listing.image_url} alt={listing.title} className="h-12 w-12 shrink-0 rounded-xl object-cover" loading="lazy" />
                  ) : (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0f766e]/10 text-[#0f766e] dark:text-[#14b8a6]">
                      <Building2 className="h-5 w-5" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-[#12263a] dark:text-[#f7f4ee]">{listing.title}</p>
                    <p className="text-xs font-semibold text-[#12263a]/50 dark:text-[#f7f4ee]/45">
                      <span className={listing.kind === "alquiler" ? "text-[#0f766e] dark:text-[#14b8a6]" : "text-[#f59e0b]"}>
                        {listing.kind === "alquiler" ? "Alquiler" : "Venta"}
                      </span>
                      {" · "}{listing.price}{" · "}{listing.city}
                    </p>
                  </div>
                  <button
                    onClick={() => removeListing(listing.id)}
                    aria-label={`Eliminar ${listing.title}`}
                    data-testid={`listing-delete-${listing.id}`}
                    className="rounded-full p-2 text-[#12263a]/35 transition-colors duration-200 hover:bg-red-500/10 hover:text-red-500 dark:text-[#f7f4ee]/35"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Panel() {
  const { user } = useAuth();

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#12263a]" data-testid="panel-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#14b8a6]" />
      </div>
    );
  }
  return user ? <Dashboard /> : <LoginForm />;
}
